<template>
  <div class="tte">
    <!-- Kopf: Vorschau des Knopfs auf dem Fahrscheindrucker, Name, ID und Tarifzone -->
    <div class="tte__head">
      <span class="tte__button" :style="previewStyle" aria-hidden="true">{{ value.abbreviation || "Buttontext" }}</span>
      <div class="tte__ident">
        <span class="tte__name">{{ value.name || (mode === "create" ? "Neue Ticketart" : "–") }}</span>
        <span class="tte__meta">
          <template v-if="mode === 'edit'">ID {{ value.ticket_type_id }} · </template>{{ zoneName }}
        </span>
      </div>
    </div>
    <p v-if="readonly" class="tte__hint">
      <app-icon name="lock" :size="14" />Diese Tarifzone ist für Ihren Mandanten nur lesbar.
    </p>

    <section class="tte__section">
      <h3 class="tte__heading">Allgemein</h3>
      <div class="tte__grid">
        <div v-if="mode === 'create'" class="app-form-field">
          <label class="app-label" :for="id('ticket_type_id')">Ticketart-ID</label>
          <app-text-field :id="id('ticket_type_id')" :value="value.ticket_type_id" inputmode="numeric" :error="errors.ticket_type_id" @input="set('ticket_type_id', $event)" />
        </div>
        <div class="app-form-field tte__wide">
          <label class="app-label" :for="id('name')">Name</label>
          <app-text-field :id="id('name')" :value="value.name" :disabled="readonly" :error="errors.name" @input="set('name', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('abbreviation')">Buttontext</label>
          <app-text-field :id="id('abbreviation')" :value="value.abbreviation" :disabled="readonly" :error="errors.abbreviation" @input="set('abbreviation', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('toggle_text')">Schaltertext</label>
          <app-text-field :id="id('toggle_text')" :value="value.toggle_text" :disabled="readonly" @input="set('toggle_text', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('button_color')">Buttonfarbe</label>
          <div class="tte__color">
            <input
              class="tte__swatch"
              type="color"
              :value="colorHex"
              :disabled="readonly"
              aria-label="Buttonfarbe wählen"
              @input="setColor($event.target.value)"
            />
            <app-text-field :id="id('button_color')" :value="value.button_color" placeholder="#3d5a80" :disabled="readonly" @input="set('button_color', $event)" />
          </div>
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('order_nr')">Sortierung</label>
          <app-text-field :id="id('order_nr')" :value="value.order_nr" inputmode="numeric" :disabled="readonly" :error="errors.order_nr" @input="set('order_nr', $event)" />
        </div>
        <div class="app-form-field tte__wide">
          <label class="app-label" :for="id('type')">Typ</label>
          <app-select :id="id('type')" block :value="kindValue" :items="kinds" :disabled="readonly" :error="errors.type" @input="set('type', $event)" />
        </div>
      </div>
    </section>

    <section class="tte__section">
      <h3 class="tte__heading">Preis</h3>
      <div class="tte__grid">
        <div class="app-form-field">
          <label class="app-label" :for="id('tariff_price_grade')">Preisstufe</label>
          <app-text-field :id="id('tariff_price_grade')" :value="value.tariff_price_grade" inputmode="numeric" :disabled="readonly" :error="errors.tariff_price_grade" @input="set('tariff_price_grade', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('additional_price')">Zusatzpreis</label>
          <app-text-field :id="id('additional_price')" :value="value.additional_price" inputmode="numeric" :disabled="readonly" :error="errors.additional_price" @input="set('additional_price', $event)" />
        </div>
      </div>
    </section>

    <section class="tte__section">
      <h3 class="tte__heading">Fahrgäste und Gültigkeit</h3>
      <div class="tte__grid">
        <div class="app-form-field">
          <label class="app-label" :for="id('min_passenger')">Min. Fahrgäste</label>
          <app-text-field :id="id('min_passenger')" :value="value.min_passenger" inputmode="numeric" :disabled="readonly" :error="errors.min_passenger" @input="set('min_passenger', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('max_passenger')">Max. Fahrgäste</label>
          <app-text-field :id="id('max_passenger')" :value="value.max_passenger" inputmode="numeric" :disabled="readonly" :error="errors.max_passenger" @input="set('max_passenger', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('seats')">Sitze</label>
          <app-text-field :id="id('seats')" :value="value.seats" inputmode="numeric" :disabled="readonly" :error="errors.seats" @input="set('seats', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('notusedby')">Nicht verwendet von</label>
          <app-text-field :id="id('notusedby')" :value="value.notusedby" :disabled="readonly" @input="set('notusedby', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('valid_from')">Gültig ab</label>
          <app-text-field :id="id('valid_from')" :value="value.valid_from" inputmode="numeric" :disabled="readonly" :error="errors.valid_from" @input="set('valid_from', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('valid_to')">Gültig bis</label>
          <app-text-field :id="id('valid_to')" :value="value.valid_to" inputmode="numeric" :disabled="readonly" :error="errors.valid_to" @input="set('valid_to', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('passnr_length')">Länge Passnummer</label>
          <app-text-field :id="id('passnr_length')" :value="value.passnr_length" inputmode="numeric" :disabled="readonly" :error="errors.passnr_length" @input="set('passnr_length', $event)" />
        </div>
        <div class="app-form-field">
          <label class="app-label" :for="id('passenger_name_length')">Länge Kundenname</label>
          <app-text-field :id="id('passenger_name_length')" :value="value.passenger_name_length" inputmode="numeric" :disabled="readonly" :error="errors.passenger_name_length" @input="set('passenger_name_length', $event)" />
        </div>
      </div>
      <app-switch-row
        class="tte__switch"
        :value="value.is_counting_relevant"
        label="Für die Zählung relevant"
        :disabled="readonly"
        @input="set('is_counting_relevant', $event)"
      />
    </section>

    <section class="tte__section">
      <div class="tte__heading-row">
        <h3 class="tte__heading">Druckdefinition</h3>
        <span class="tte__lines">{{ lineCount }} {{ lineCount === 1 ? "Zeile" : "Zeilen" }}</span>
      </div>
      <!-- Einfügehilfen: Tag bzw. Variable wählen und an der Cursorposition einfügen -->
      <div v-if="!readonly" class="tte__insert">
        <app-select block :value="tag" :items="tagItems" prefix="Tag" :menu-width="280" @input="tag = $event" />
        <app-button icon="plus" @click="insert(tag)">Einfügen</app-button>
        <app-select block :value="variable" :items="variableItems" prefix="Variable" :menu-width="240" @input="variable = $event" />
        <app-button icon="plus" @click="insert(variable)">Einfügen</app-button>
      </div>
      <textarea
        :id="id('print_definition')"
        ref="print"
        class="tte__print"
        :value="value.print_definition"
        :readonly="readonly"
        rows="16"
        spellcheck="false"
        aria-label="Druckdefinition"
        @input="set('print_definition', $event.target.value)"
      ></textarea>
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs „Ticketart bearbeiten“ bzw. „Ticketart anlegen“ (Kundenwunsch 13.10.2026: „bitte einmal
// die bearbeitung der ticketarten grundlegend überarbeiten und an unser neues design anpassen! das gefällt mir so gar
// nicht!“; ersetzt das DevExtreme-Popup mit vierspaltigem Formular). Kopf mit Vorschau des Knopfs (Buttontext auf
// Buttonfarbe, Schrift hell oder dunkel nach Helligkeit), dann Abschnitte Allgemein (Name, Buttontext, Schaltertext,
// Buttonfarbe mit Farbwähler, Sortierung, Typ), Preis (Preisstufe, Zusatzpreis), Fahrgäste und Gültigkeit (min./max.
// Fahrgäste, Sitze, Nicht verwendet von, Gültig ab/bis, Längen, Schalter Zählung) und Druckdefinition (Festbreiten-
// schrift, Tag bzw. Variable an der Cursorposition einfügen). Reine Darstellung: der Entwurf (value) gehört der Seite;
// jede Eingabe meldet field(key, wert). readonly (Tarifzone nur lesbar): alles gesperrt, ohne Einfügehilfen.
import { TICKET_TYPE_KINDS, PRINT_TAGS, PRINT_VARIABLES } from "@/components/masterdata/ticketTypeOptions";

const HEX = /^#?([0-9a-f]{6})$/i;

export default {
  name: "TicketTypeEditor",
  props: {
    value: { type: Object, required: true },
    mode: { type: String, default: "edit" },
    readonly: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    zoneName: { type: String, default: "" },
  },
  data() {
    return {
      kinds: TICKET_TYPE_KINDS.map((k) => ({ value: k.value, text: k.value + " · " + k.text })),
      tagItems: PRINT_TAGS.map((t) => ({ value: t.code, text: t.code + "  " + t.text })),
      variableItems: PRINT_VARIABLES.map((v) => ({ value: v, text: v })),
      tag: PRINT_TAGS[0].code,
      variable: PRINT_VARIABLES[0],
    };
  },
  computed: {
    kindValue() {
      const n = Number(this.value.type);
      return this.value.type === "" || this.value.type === null || !Number.isFinite(n) ? null : n;
    },
    colorHex() {
      const m = HEX.exec(String(this.value.button_color || "").trim());
      return m ? "#" + m[1].toLowerCase() : "#3d5a80";
    },
    // Knopfvorschau: Fläche Buttonfarbe (sonst neutral), Schrift weiß oder dunkel nach Helligkeit
    previewStyle() {
      const m = HEX.exec(String(this.value.button_color || "").trim());
      if (!m) {
        return null;
      }
      const hex = m[1];
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const light = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
      return { background: "#" + hex, borderColor: "#" + hex, color: light ? "#151a21" : "#fff" };
    },
    lineCount() {
      const text = String(this.value.print_definition || "");
      return text ? text.split("\n").length : 0;
    },
  },
  methods: {
    id(key) {
      return "tte-" + this._uid + "-" + key;
    },
    set(key, value) {
      this.$emit("field", key, value);
    },
    // Farbwähler: Format der vorhandenen Angabe beibehalten (mit oder ohne #)
    setColor(hex) {
      const current = String(this.value.button_color || "").trim();
      this.set("button_color", current && current[0] !== "#" ? hex.slice(1) : hex);
    },
    // Tag bzw. Variable an der Cursorposition einfügen (ersetzt eine Markierung)
    insert(text) {
      const el = this.$refs.print;
      if (!el || !text) {
        return;
      }
      const current = String(this.value.print_definition || "");
      const start = typeof el.selectionStart === "number" ? el.selectionStart : current.length;
      const end = typeof el.selectionEnd === "number" ? el.selectionEnd : current.length;
      this.set("print_definition", current.slice(0, start) + text + current.slice(end));
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + text.length;
      });
    },
  },
};
</script>

<style scoped>
.tte {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.tte__head {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
/* Knopf wie auf dem Fahrscheindrucker: Buttontext auf Buttonfarbe */
.tte__button {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  max-width: 140px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid var(--edge);
  border-radius: 8px;
  background: var(--tint);
  color: var(--ink);
  font-size: 13px;
  line-height: 16px;
  font-weight: 600;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-shadow: var(--sh-ctl);
}
.tte__ident {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tte__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.tte__meta {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
p.tte__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 8px 12px;
  border-radius: var(--r-ctl);
  background: var(--band);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
/* Abschnitte wie im Verkaufsdetail */
.tte__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
.tte__heading-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
h3.tte__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.tte__lines {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
/* Felder zweispaltig, breite Felder über beide Spalten */
.tte__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.tte__wide {
  grid-column: 1 / -1;
}
@media (max-width: 599.98px) {
  .tte__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
.tte__color {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tte__color > :last-child {
  flex: 1 1 auto;
  min-width: 0;
}
.tte__swatch {
  flex: none;
  width: var(--h-ctl);
  height: var(--h-ctl);
  box-sizing: border-box;
  padding: 2px;
  border: 1px solid var(--field-edge);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
  cursor: pointer;
}
.tte__swatch:disabled {
  cursor: default;
  opacity: .6;
}
.tte__switch {
  border-top: 1px solid var(--line-2);
}
/* zwei Zeilen: Auswahl über die Breite, „Einfügen“ daneben */
.tte__insert {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
}
/* Druckdefinition in Festbreitenschrift wie ein Code-Feld */
.tte__print {
  display: block;
  width: 100%;
  min-height: 280px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid var(--field-edge);
  border-radius: var(--r-ctl);
  background: var(--band);
  color: var(--ink);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  line-height: 18px;
  resize: vertical;
  white-space: pre;
  overflow-wrap: normal;
  overflow-x: auto;
}
.tte__print:hover:not([readonly]) {
  border-color: var(--field-edge-hover);
}
.tte__print:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
</style>
